import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Folder } from "lucide-react";
import { FeedbackState } from "@h2j-data-team/ui";

import { ListError, ListLoading } from "@/components/qvd-analysis/qvd-list-parts";
import {
  DirectoryLevel,
  UsageTable,
  type UsageRow,
} from "@/components/qvd-analysis/SpaceDirectoryLevel";
import { formatBytes } from "@/lib/health-format";
import {
  capacityDirectoriesQueryOptions,
  capacitySpacesQueryOptions,
} from "@/features/capacity/api";

// CapacityBySpaceView aggregates the measured day's data-file consumption by
// space, each space drilling into its folder tree (local state — a folder is not
// worth a URL, and the caller remounts this on snapshot change so no path leaks
// from one connection to the next).
export function CapacityBySpaceView({ snapshotId }: { snapshotId: string }) {
  const { t } = useTranslation("capacity");
  const { data, isLoading, isError, error, refetch } = useQuery(
    capacitySpacesQueryOptions(snapshotId, "datafile"),
  );
  const [drill, setDrill] = useState<{ spaceName: string; prefix: string } | null>(null);
  // Always called, even when no space is open: the query is disabled without one.
  const directory = useQuery(
    capacityDirectoriesQueryOptions(snapshotId, drill?.spaceName ?? "", drill?.prefix ?? ""),
  );

  const spaces = data?.data;
  // Measured rows whose space never resolved: no space to open, but they are
  // part of the same population the flat list shows, so the view names them
  // instead of silently totalling less than the tab beside it.
  const unassigned = data?.unassigned ?? { bytes: 0, fileCount: 0 };
  const total = useMemo(() => (spaces ?? []).reduce((sum, s) => sum + s.bytes, 0), [spaces]);
  const rows = useMemo<UsageRow[]>(
    () =>
      (spaces ?? []).map((s) => ({
        key: s.spaceName,
        name: s.spaceName,
        kind: "space",
        fileCount: s.fileCount,
        bytes: s.bytes,
      })),
    [spaces],
  );
  const headers = useMemo(
    () => ({
      name: t("bySpace.cols.space"),
      files: t("bySpace.cols.files"),
      size: t("bySpace.cols.size"),
      pct: t("bySpace.cols.pct"),
    }),
    [t],
  );

  // The space list stays mounted (hidden) while a space is open, so its search
  // and sort are still there on the way back.
  const directoryView = drill ? (
    <DirectoryLevel
      spaceName={drill.spaceName}
      prefix={drill.prefix}
      onPrefix={(prefix) => setDrill({ spaceName: drill.spaceName, prefix })}
      onExit={() => setDrill(null)}
      query={directory}
      texts={{
        label: t("byFile.cols.file"),
        path: t("bySpace.path"),
        backToSpaces: t("bySpace.backToSpaces"),
        up: t("bySpace.up"),
        openDir: (name) => t("bySpace.openDir", { name }),
        error: t("bySpace.error"),
        emptyDir: t("bySpace.emptyDir"),
        summary: (d) =>
          t("bySpace.dirSummary", {
            files: d.totalFiles.toLocaleString(),
            size: formatBytes(d.totalBytes),
          }),
        truncated: (count) => t("bySpace.truncated", { limit: count.toLocaleString() }),
        headers: { ...headers, name: t("bySpace.cols.name") },
      }}
    />
  ) : null;

  if (isLoading) return <ListLoading />;
  if (isError) {
    return <ListError error={error} title={t("error.load")} onRetry={() => void refetch()} />;
  }
  // No space at all, but rows in the population: this snapshot was collected
  // before the space was recorded on data-file rows — recollecting is the fix,
  // and saying so beats a message that only restates the emptiness.
  if (!spaces || spaces.length === 0) {
    return (
      <FeedbackState
        state="empty"
        icon={<Folder />}
        title={unassigned.fileCount > 0 ? t("bySpace.emptyNoSpaceData") : t("bySpace.empty")}
      />
    );
  }

  return (
    <>
      {directoryView}
      <div hidden={drill !== null}>
        <UsageTable
          label={t("view.bySpace")}
          rows={rows}
          total={total}
          headers={headers}
          emptyTitle={t("bySpace.empty")}
          openLabel={(name) => t("bySpace.openSpace", { name })}
          onOpen={(row) => setDrill({ spaceName: row.name, prefix: "" })}
          footer={
            unassigned.fileCount > 0 ? (
              <p className="text-xs text-faint">
                {t("bySpace.unassigned", {
                  count: unassigned.fileCount,
                  size: formatBytes(unassigned.bytes),
                })}
              </p>
            ) : undefined
          }
        />
      </div>
    </>
  );
}
